import React, { FC } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';

const SkeletonCard = styled(Card)(({ theme }) => ({
  transition: 'none', // Remove transitions for skeleton
  cursor: 'default',
  marginBottom: theme.spacing(1),
}));

/**
 * Skeleton component for lead cards during loading states
 * Mimics the structure and visual hierarchy of the real LeadCardComponent
 *
 * @returns {JSX.Element} Skeleton loading component for lead cards
 */
const LeadCardSkeleton: FC = () => {
  return (
    <SkeletonCard>
      <CardContent sx={{ padding: 2 }}>
        {/* Header: Name and time */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Skeleton variant="text" width="65%" height={24} sx={{ fontWeight: 'bold' }} />
          <Skeleton variant="text" width="15%" height={16} />
        </Box>

        {/* Phone number */}
        <Box display="flex" alignItems="center" mb={1}>
          <Skeleton variant="text" width="20px" height={20} sx={{ mr: 0.5, borderRadius: '2px' }} />
          <Skeleton variant="text" width="70%" height={20} />
        </Box>

        {/* Car brand and model */}
        <Box display="flex" alignItems="center" mb={1}>
          <Skeleton variant="text" width="20px" height={20} sx={{ mr: 0.5, borderRadius: '2px' }} />
          <Skeleton variant="text" width="60%" height={20} />
        </Box>

        {/* Messages count - only show sometimes to vary the skeleton */}
        {Math.random() > 0.4 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        )}

        {/* Source chip - only show sometimes */}
        {Math.random() > 0.3 && (
          <Box mt={1}>
            <Skeleton
              variant="rectangular"
              width={Math.random() * 40 + 50} // Variable width between 50-90px
              height={24}
              sx={{ borderRadius: '12px' }}
            />
          </Box>
        )}
      </CardContent>
    </SkeletonCard>
  );
};

interface LeadCardSkeletonGroupProps {
  count: number;
  delay: number;
}

/**
 * Component for rendering multiple skeleton cards with staggered animation
 *
 * @param {Object} props - Component props
 * @param {number} props.count - Number of skeleton cards to render
 * @param {number} [props.delay=100] - Delay between each skeleton animation (ms)
 * @returns {JSX.Element} Multiple skeleton cards with staggered loading
 */
export const LeadCardSkeletonGroup: FC<LeadCardSkeletonGroupProps> = ({ count, delay = 100 }) => {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Box
          key={`skeleton-card-${index}`}
          sx={{
            animation: 'fadeIn 0.3s ease-in-out',
            animationDelay: `${index * delay}ms`,
            animationFillMode: 'both',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <LeadCardSkeleton />
        </Box>
      ))}
    </>
  );
};

export default LeadCardSkeleton;
